import React, { ReactNode, useMemo, useState } from "react";
import {
  Button,
  useMediaQuery,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import GestureIcon from "@mui/icons-material/Gesture";
import styled from "@emotion/styled";
import { useRangeSelectControl, useChartColors } from "@hooks";
import { InvestmentChartData } from "@helpers/charts/mergeCloseByDate";
import { RangeSelector } from "@components/common";
import { ChartLayout, Figure, ComparisonValue } from "../BaseChartTools";
import { getEventLabelForEvents } from "../highcharts/DrawLineChart/getEventLabel";
import { DrawLineChart } from "../highcharts";
import { prepareInvestmentsData } from "./utils";
import { InvestmentChartBottomBar } from "./InvestmentChartBottomBar";

const ChartActionsContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
`;

const DrawLinesButton = styled(Button)`
  padding: 3px 5px;
  width: max-content;
  height: max-content;
  border-radius: 8px;
`;

export interface InvestmentChartProps {
  title?: string | ReactNode;
  subtitle?: ReactNode;
  data: InvestmentChartData[];
  availableDateRanges?: IntervalType[];
  availableCompactDateRanges?: IntervalType[];
  className?: string;
  onSelect?: (type: IntervalType, value?: RangeType) => void;
  isFetching?: boolean;
  bottomBarComponent?: ReactNode;
  showEvents?: boolean;
  drawLines?: boolean;
  comparedTicker?: ComparisonValue | null;
  onComparedTickerChange?: (ticker?: ComparisonValue) => void;
  showComparisonSelector?: boolean;
  customRange?: RangeType;
  mainValueLabel?: string;
  noDataMessage?: ReactNode;
  showNavigator?: boolean;
  showInCard?: boolean;
  chartHeight?: number;
  currency?: string;
}

export const InvestmentChart = ({
  data,
  className,
  onSelect,
  isFetching,
  title,
  subtitle,
  bottomBarComponent,
  showEvents = false,
  drawLines = false,
  comparedTicker,
  availableDateRanges,
  availableCompactDateRanges,
  onComparedTickerChange,
  showComparisonSelector = true,
  customRange,
  mainValueLabel,
  noDataMessage,
  showNavigator = true,
  showInCard = false,
  chartHeight,
  currency,
}: InvestmentChartProps) => {
  const theme = useTheme();
  const securitiesChartColors = useChartColors("securitiesChart");
  const chartColors = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(securitiesChartColors).filter(
          ([, value]) => typeof value === "string"
        )
      ) as Record<string, string>,
    [securitiesChartColors]
  );
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const { range, intervalType, customIntervalSelect, selectInterval } =
    useRangeSelectControl({
      onSelect,
      firstDate: data[0] ? data[0].date : new Date(),
    });

  const [lineDrawingEnabled, setLineDrawingEnabled] = useState(false);
  const [isChartHovering, setIsChartHovering] = useState(false);

  const isComparison = Boolean(comparedTicker);

  const markers = useMemo(
    () =>
      showEvents
        ? data
            .filter((x) => Boolean(x.events?.length))
            .map((x) => {
              const label = getEventLabelForEvents(x.events!, theme.palette);
              return {
                x: x.date.getTime(),
                title: label.name,
                style: {
                  color: label.color,
                },
                color: theme.palette.grey[400],
                data: [x.date.getTime(), x.eventsValue ?? 0] as [
                  number,
                  number,
                ],
                tooltipData: x.events,
              };
            })
        : [],
    [data, showEvents, theme.palette]
  );

  const chartData = useMemo(
    () =>
      prepareInvestmentsData(
        data,
        chartColors,
        isComparison,
        Boolean(comparedTicker),
        isFetching
      ),
    [data, chartColors, isComparison, comparedTicker, isFetching]
  );

  return (
    <ChartLayout
      isFetching={isFetching}
      useCard={showInCard}
      className={className}
      intervalSelector={
        !customRange ? (
          <RangeSelector
            compact={isXs}
            range={intervalType === "custom" ? range : undefined}
            intervalType={intervalType}
            intervalTypes={availableDateRanges}
            compactIntervalTypes={availableCompactDateRanges}
            onSelect={selectInterval}
            onCustomSelect={customIntervalSelect}
          />
        ) : null
      }
      figure={
        title || subtitle ? (
          <Figure>
            {typeof title === "string" ? (
              <Typography variant={isXs ? "h4" : "h2"}>{title}</Typography>
            ) : (
              title
            )}
            {subtitle}
          </Figure>
        ) : null
      }
      bottomComponent={
        <InvestmentChartBottomBar
          disabled={intervalType === "oneDay"}
          selectedComparedValue={comparedTicker}
          onComparedValueChange={onComparedTickerChange}
          bottomBarComponent={bottomBarComponent}
          showComparisonSelector={showComparisonSelector}
        />
      }
      onChartMouseEnter={() => {
        setIsChartHovering(true);
      }}
      onChartMouseLeave={() => {
        setIsChartHovering(false);
      }}
    >
      <DrawLineChart
        allowDrawing={lineDrawingEnabled}
        chartData={chartData}
        chartType={isComparison ? "performance" : "valuation"}
        markers={markers}
        mainValueLabel={mainValueLabel}
        comparedTicker={comparedTicker ?? undefined}
        noDataMessage={noDataMessage}
        showNavigator={showNavigator}
        chartHeight={chartHeight}
        currency={currency}
      />
      {(isChartHovering || lineDrawingEnabled) && drawLines && (
        <ChartActionsContainer>
          <DrawLinesButton
            variant="tertiary"
            startIcon={<GestureIcon />}
            size="small"
            onClick={() => setLineDrawingEnabled(!lineDrawingEnabled)}
          >
            {lineDrawingEnabled ? "Clear" : "Draw"}
          </DrawLinesButton>
        </ChartActionsContainer>
      )}
    </ChartLayout>
  );
};
